import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import SolucoesCrossLink from "@/components/feature/SolucoesCrossLink";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/base/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import FeatureGrid from "@/components/feature/FeatureGrid";
import ProcessSteps from "@/components/feature/ProcessSteps";
import FaqAccordion from "@/components/feature/FaqAccordion";
import MultilingualSection from "@/components/feature/MultilingualSection";
import SpeedComparison from "@/components/feature/SpeedComparison";
import DemoBanner from "@/components/feature/DemoBanner";
import FinalCta from "@/pages/home/components/FinalCta";
import { whatsappLink } from "@/constants/site";
import { storeFeatures, storeTypes, storeSteps, lojasFaqs } from "@/mocks/lojas";

export default function LojasVirtuais() {
  return (
    <SiteLayout>
      <Seo
        title="Criação de Loja Virtual e E-commerce para Empresas | NXHOST"
        description="Criamos lojas virtuais e e-commerces modernos e responsivos, com catálogo de produtos, carrinho, checkout otimizado e integração com WhatsApp. Venda mais pela internet com a NXHOST."
        keywords="loja virtual, e-commerce, criar loja virtual, loja online, catálogo de produtos, vender online, loja multi-idiomas"
        path="/lojas-virtuais"
        titleEn="Online Store & E-commerce Creation for Companies | NXHOST"
        titleEs="Creación de Tienda Online y E-commerce para Empresas | NXHOST"
        descriptionEn="We build modern, responsive online stores with product catalog, cart, checkout and WhatsApp integration. Sell online with NXHOST."
        descriptionEs="Creamos tiendas online modernas y responsivas con catálogo, carrito, checkout e integración con WhatsApp. Vende por internet con NXHOST."
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "Lojas Virtuais", path: "/lojas-virtuais" },
        ]}
        faqs={lojasFaqs}
      />

      <PageHero
        badge="Lojas virtuais"
        badgeIcon="ri-shopping-bag-3-line"
        title="Transforme a sua presença digital em uma experiência de venda."
        subtitle="Criamos lojas virtuais e e-commerces modernos, responsivos e prontos para apresentar os seus produtos, facilitar a compra pelo celular e vender pela internet com profissionalismo."
        crumbs={[{ label: "Início", to: "/" }, { label: "Lojas Virtuais" }]}
      >
        <Reveal delay={220}>
          <div className="rounded-2xl border border-background-50/10 bg-foreground-900/60 p-5 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-background-400">
                Sua loja virtual
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-secondary-400">
                <span className="h-2 w-2 rounded-full bg-secondary-400"></span>
                Aberta
              </span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="font-heading text-2xl font-bold text-background-50">Catálogo</p>
                <p className="mt-1 text-[11px] text-background-300">Produtos organizados</p>
              </div>
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="font-heading text-2xl font-bold text-background-50">Carrinho</p>
                <p className="mt-1 text-[11px] text-background-300">Compra facilitada</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-background-50/10 bg-background-50/5 p-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-500/15 text-secondary-400">
                  <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                </span>
                <p className="text-sm font-semibold text-background-50">Pedido via WhatsApp</p>
              </div>
              <i className="ri-arrow-right-line text-background-300" aria-hidden="true"></i>
            </div>
          </div>
        </Reveal>
      </PageHero>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="O que a sua loja tem"
            badgeIcon="ri-store-3-line"
            title="Tudo o que uma loja virtual precisa para vender."
            description="Uma loja virtual completa para apresentar os seus produtos, facilitar a compra pelo celular e acompanhar vendas, pedidos e resultados em um só lugar."
          />
          <div className="mt-12">
            <FeatureGrid items={storeFeatures} columns={3} accent="primary" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="relative">
                <div className="overflow-hidden rounded-2xl border border-background-200">
                  <img
                    src="https://storage.helloreaddy.io/project_files/9d609ae5-b03e-4dcf-a75d-08eaee046d5d/d3ee3775-86e1-44af-a734-34a2ee50e57b_compressed_loja-virtual-profissional.webp"
                    alt="Loja virtual e e-commerce modernos exibidos em notebook e celular"
                    title="Criação de loja virtual e e-commerce NXHOST"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-5 -left-3 hidden rounded-xl border border-background-200 bg-background-50 p-4 sm:block animate-floaty">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                      <i className="ri-shopping-cart-2-line text-xl" aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-[11px] text-foreground-500">Compra fácil</p>
                      <p className="text-sm font-semibold text-foreground-950">Checkout otimizado</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
                  <i className="ri-smartphone-line" aria-hidden="true"></i>
                  Experiência de compra
                </span>
                <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
                  Uma loja pensada para quem compra pelo celular.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-foreground-600">
                  A maior parte das compras online acontece no celular. Por isso, criamos lojas virtuais
                  com navegação simples, páginas rápidas e um caminho curto até a finalização do pedido,
                  reduzindo o abandono de carrinho e aumentando as chances de venda.
                </p>
                <ul className="mt-8 space-y-3">
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-check-double-line text-secondary-600" aria-hidden="true"></i>
                    Navegação simples e rápida
                  </li>
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-check-double-line text-secondary-600" aria-hidden="true"></i>
                    Produtos organizados por categoria
                  </li>
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-check-double-line text-secondary-600" aria-hidden="true"></i>
                    Carrinho e checkout objetivos
                  </li>
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <i className="ri-check-double-line text-secondary-600" aria-hidden="true"></i>
                    Contato rápido pelo WhatsApp
                  </li>
                </ul>
                <a
                  href={whatsappLink("Ola! Quero criar uma loja virtual com a NXHOST.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-9 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  Criar minha loja
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <MultilingualSection message="Ola! Quero uma loja virtual multi-idiomas (portugues, ingles e espanhol) com a NXHOST." />

      <SpeedComparison />

      <DemoBanner message="Ola! Quero uma loja virtual de demonstracao sem custo para a minha empresa." />

      <section className="relative overflow-hidden bg-foreground-950 py-16 text-background-50 md:py-24">
        <div className="absolute inset-0 nx-grid opacity-25"></div>
        <div className="nx-glow absolute -bottom-20 left-1/4 h-[420px] w-[420px] animate-pulseGlow"></div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Segmentos"
            badgeIcon="ri-store-2-line"
            title="Uma loja virtual para cada tipo de negócio."
            description="Estruturas de loja virtual adaptadas ao jeito de vender de cada segmento, com catálogo, categorias e experiência de compra sob medida."
            tone="dark"
          />
          <div className="mt-12">
            <FeatureGrid items={storeTypes} tone="dark" columns={3} accent="accent" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Como funciona"
            badgeIcon="ri-route-line"
            badgeTone="accent"
            title="Do planejamento à loja no ar."
            description="Um processo claro, do entendimento do seu negócio ao lançamento da loja e acompanhamento dos primeiros pedidos."
          />
          <div className="mt-12">
            <ProcessSteps steps={storeSteps} />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Dúvidas frequentes"
            badgeIcon="ri-question-line"
            title="Perguntas sobre lojas virtuais."
          />
          <div className="mt-12">
            <FaqAccordion items={lojasFaqs} />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <SectionHeading
                badge="Loja virtual NXHOST"
                badgeIcon="ri-shopping-bag-3-line"
                align="left"
                title="Uma loja virtual completa, do catálogo à venda."
              />
              <div className="mt-6 space-y-4 text-base leading-relaxed text-foreground-600">
                <p>
                  Uma <strong className="font-semibold text-foreground-950">loja virtual</strong> reúne
                  tudo o que a sua empresa precisa para vender pela internet: catálogo de produtos
                  organizado por categorias, páginas de produto claras, carrinho, checkout e canais de
                  contato como o WhatsApp. Na NXHOST, construímos e-commerces pensados para carregar
                  rápido e funcionar bem em qualquer tela.
                </p>
                <p>
                  Além de criar a loja, cuidamos da base que sustenta as vendas:{" "}
                  <Link
                    to="/hospedagem"
                    className="font-semibold text-primary-600 transition-colors hover:text-primary-700"
                  >
                    hospedagem de sites
                  </Link>{" "}
                  rápida e estável,{" "}
                  <Link
                    to="/dominios"
                    className="font-semibold text-primary-600 transition-colors hover:text-primary-700"
                  >
                    registro de domínio
                  </Link>{" "}
                  próprio e uma estrutura de{" "}
                  <Link
                    to="/sites"
                    className="font-semibold text-primary-600 transition-colors hover:text-primary-700"
                  >
                    criação de sites
                  </Link>{" "}
                  pensada para transformar visitantes em clientes.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
              <h3 className="font-heading text-base font-semibold text-foreground-950">
                Uma loja virtual para vender mais
              </h3>
              <ul className="mt-5 space-y-3">
                {[
                  "Catálogo de produtos com categorias e variações",
                  "Experiência de compra otimizada para o celular",
                  "Checkout simples, direto e sem distrações",
                  "Integração com WhatsApp para pedidos e dúvidas",
                  "Estrutura de SEO para os produtos aparecerem no Google",
                  "Base preparada para crescer junto com a sua loja",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-double-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <SolucoesCrossLink
        title="Loja virtual integrada às demais soluções digitais da NXHOST"
        description="Hospedagem, domínio, criação de sites e marketing trabalhando juntos para a sua loja vender mais. Veja como combinar os serviços."
        links={[
          { label: "Hospedagem de sites", to: "/hospedagem" },
          { label: "Criação de sites", to: "/sites" },
          { label: "Registro de domínio", to: "/dominios" },
          { label: "SEO e Google", to: "/solucoes" },
        ]}
      />

      <FinalCta />
    </SiteLayout>
  );
}